/**
 * ESG Stewardship Intelligence Component
 * Trust Tech Studio™ | Sovereign Intelligence Dashboard
 *
 * Comprehensive sovereign intelligence dashboard providing:
 * - IPPO Execution Nodes navigation
 * - Stabilization Path: Native Trust Digital Frontier (3-phase pipeline)
 * - Account Dashboard: The People's Market (TTD) with live metrics
 * - Portfolio Management: Genesis 13 Stakeholder View ($31.025B AUM)
 * - Governance Rights Menu
 * - Business Intelligence: Old World vs Native Trust
 * - Clean Room Protocol: Gate 1 & 2 Execution
 *
 * @version 2.0.0
 * @updated 2026-02-05
 */

import React, { useState } from 'react';

/* ─────────────────────────── Data ─────────────────────────── */

const marketMetrics = {
  marketCap: '$3.00T',
  globalEconomy: '2.61%',
  symbol: 'TTD',
  status: 'Sovereign',
  ttdPrice: { value: '3,000.00', change: '+0.26%' },
  trustIndex: { value: '98.00', change: '+2.00%' },
  esgIndex: { value: '95.00', change: '+5.00%' },
  totalVolume: '+12.5%',
  activeTraders: '+15.7%',
  supplierNodes: { count: '80K+', change: '+15.00%' }
};

const genesisPartners = [
  { partner: 'NVIDIA', identity: 'Sovereign AI Lead', managed: '$7.48B (HQLA Locked)', exposure: '$0 (Air-Gapped)', status: 'Aligned', color: 'success' },
  { partner: 'Microsoft', identity: 'G-Deficit Priority', managed: '$3.15B (Azure Migration)', exposure: '$5.20B (P0 Risk)', status: 'Escalated', color: 'danger' },
  { partner: 'Anthropic', identity: 'Safety & Ethics', managed: '$2.20B (Sovereign Rail)', exposure: '$420M (Old World)', status: 'Verified', color: 'success' },
  { partner: 'Amazon (AWS)', identity: 'Cloud Capacity', managed: '$4.22B (New Frontier)', exposure: '$640M (Legacy Debt)', status: 'Locked', color: 'success' },
  { partner: 'Google (Alphabet)', identity: 'Compute Engine', managed: '$1.85B (TTD Aligned)', exposure: '$110M (Compliance)', status: 'Active', color: 'success' },
  { partner: 'IBM', identity: 'L0 Data Custodian', managed: '$2.45B (IPPO Hub)', exposure: '$0 (New World)', status: 'Locked', color: 'success' },
  { partner: 'Oracle', identity: 'Database Anchor', managed: '$1.28B (Registry)', exposure: '$180M (Legacy)', status: 'Mitigating', color: 'warning' },
  { partner: 'Intel', identity: 'Hardware Logic', managed: '$1.45B (80K Nodes)', exposure: '$210M (Legacy Firm)', status: 'Staging', color: 'warning' },
  { partner: 'HPE', identity: 'Infrastructure Host', managed: '$1.92B (ESG Aligned)', exposure: '$300M (Old World)', status: 'Aligned', color: 'success' },
  { partner: 'Dell', identity: 'Edge Deployment', managed: '$1.36B (1687 Anchor)', exposure: '$0 (New World)', status: 'Verified', color: 'success' },
  { partner: 'Meta', identity: 'Interaction Layer', managed: '$1.10B (Stewardship)', exposure: '$50M (Admin)', status: 'Active', color: 'success' },
  { partner: 'AMD', identity: 'Compute Alternate', managed: '$2.10B (Digital Trust)', exposure: '$890M (G-Deficit)', status: 'Escalated', color: 'danger' },
  { partner: 'Cisco', identity: 'Network Fabric', managed: '$1.36B (Protocol Rail)', exposure: '$0 (New World)', status: 'Locked', color: 'success' }
];

const governanceRights = [
  { title: 'Right of Technical Audit', icon: 'bi-cpu', description: 'Direct access to Digital Trust GPU clusters; bypass Azure P0 vulnerabilities.' },
  { title: 'Right of Redirection', icon: 'bi-arrow-left-right', description: 'Deploy the 9-406 "Hash of Sovereignty" to move capital from legacy corporate silos.' },
  { title: 'Right of the Badge', icon: 'bi-award', description: 'Embed blockchain-linked TTD Share Badge across brand touchpoints for verified trust.' },
  { title: 'Right of Stewardship', icon: 'bi-shield-lock', description: 'View your 1687 Club NFT Qualifying CER  Purchase and learn more about your Stewardship Rights.' },
  { title: 'Right of Steering Committee Engagement', icon: 'bi-shield-check', description: 'Each Steward must attend monthly events with other Genesis Partners and vote on systemic integrity mediation flow.', link: 'https://www.kromarketplace.com/governance-hub?view=enforcement' }
];

const ippoNodes = [
  { group: 'K-R-O', items: ['IPPO Trading Hub', 'Marketplace', 'Reports', 'Data Collection'] },
  { group: 'Intelligence', items: ['Raw Intelligence', 'Predictive Verification', 'Technical Audits'] },
  { group: 'Business Intelligence', items: ['G-Deficit Analysis', 'Performance Metrics', 'Stakeholder Views'] },
  { group: 'Governance', items: ['Governance Rights', '1687 Anchor'] }
];

const damlWorkflow = [
  { step: 1, title: 'Marketplace', icon: 'bi-shop', color: '#7c3aed', description: 'Asset origination and listing on the K-R-O Marketplace. Stakeholders discover, evaluate, and initiate trust-verified transactions.', link: 'https://www.kromarketplace.com/fair-trade-coffee-beans' },
  { step: 2, title: 'TTD', icon: 'bi-currency-exchange', color: '#2563eb', description: 'The People\'s Market (TTD) handles sovereign tokenization, pricing, and trade execution for verified real-world assets.', link: 'https://www.kromarketplace.com/ttd-portal' },
  { step: 3, title: 'Trust Tech Studio', icon: 'bi-grid-1x2', color: '#0d9488', description: 'ESG compliance, stewardship intelligence, and governance tooling. DAML smart contracts are authored and managed here.', link: 'https://www.krostudio.org/' },
  { step: 4, title: 'Canton', icon: 'bi-diagram-3', color: '#d97706', description: 'Canton distributed ledger executes DAML contracts with privacy-preserving multi-party workflows and atomic settlement guarantees.', link: 'https://www.canton.network/' },
  { step: 5, title: 'Trust Index', icon: 'bi-shield-fill-check', color: '#059669', description: 'Final trust scoring, provenance verification, and immutable audit trail. Performance rights and ESG indices are published on-chain.', link: 'https://www.kromarketplace.com/reports' }
];

const settlementStages = [
  { stage: 1, title: 'Asset Tokenized', status: 'Completed', date: '2026-01-15', icon: 'bi-coin', color: 'success', details: 'Asset digitized and tokenized on-chain. Unique token ID minted with embedded metadata, provenance, and ownership chain recorded on the distributed ledger.' },
  { stage: 2, title: 'Asset Value & Verification', status: 'Completed', date: '2026-01-22', icon: 'bi-patch-check', color: 'success', details: 'Independent valuation completed. Asset authenticity and value verified against market benchmarks. Third-party attestation secured.' },
  { stage: 3, title: 'Assignment of Rights for Settlement', status: 'Completed', date: '2026-02-01', icon: 'bi-file-earmark-lock', color: 'success', details: 'Legal assignment of rights executed. UCC-1 filing and 9-406 notification dispatched. Transfer of beneficial interest recorded in Registry Safe Harbor.' },
  { stage: 4, title: 'ISO RFP', status: 'Tested (Mercury)', date: '2026-02-06', icon: 'bi-send', color: 'info', details: 'ISO 20022 Request for Payment tested and validated via Mercury. Structured payment message generated with settlement instructions, compliance tags, and routing data. Now migrating to Canton.' },
  { stage: 5, title: 'ISO Acceptance and Payment Instructions', status: 'Migrating to Canton', date: '2026-02-10', icon: 'bi-bank', color: 'info', details: 'Counterparty acceptance flow validated in Mercury test environment. Payment instructions (Fedwire/SWIFT) with confirmed wire coordinates now being ported to Canton settlement infrastructure.' },
  { stage: 6, title: 'Final Settlement & Reconciliation', status: 'Migrating to Canton', date: '2026-02-14', icon: 'bi-check-circle', color: 'info', details: 'Reconciliation logic tested in Mercury. Full audit trail, settlement confirmation, and immutable record commit now transitioning to Canton distributed ledger for production execution.' }
];

const settlementAuditLog = [
  { timestamp: '2026-06-01', actor: 'Canton / IS', action: 'Settlement Pilot Launch w/ Infra Singularity (IS)', severity: 'info' },
  { timestamp: '2026-05-29', actor: 'Infrastructure', action: 'Infra Singularity (IS) Node Service Agreement executed', severity: 'success' },
  { timestamp: '2026-05-27', actor: 'Canton Network', action: 'Canton Network Approved Validator', severity: 'success' },
  { timestamp: '2026-05-26', actor: 'Canton Foundation', action: 'Canton Foundation Alignment on Pilot Specs', severity: 'info' },
  { timestamp: '2026-05-20', actor: 'Canton Foundation', action: 'Canton Foundation General Member', severity: 'success' },
  { timestamp: 'Q1 2026', actor: 'Provenance', action: 'Provenance Mainnet Native Tokens Anchored (RWAs)', severity: 'success' },
  { timestamp: 'Q1 2026', actor: 'Figure Markets', action: 'Figure Markets Enterprise Onboarding', severity: 'info' },
  { timestamp: '2025-11-18', actor: 'Inngot Sollmon UK', action: 'IP Valuation — Relief from royalty established', severity: 'success' },
  { timestamp: '2025-10-31', actor: 'Trust Tech SPC', action: 'Trust Tech SPC Restructuring Launch', severity: 'info' },
  { timestamp: '2025-10-25', actor: 'Perkins Coie', action: 'Genesis Design IP Patent filed', severity: 'success' }
];

/* ─────────────────────────── Helpers ─────────────────────────── */

const statusIcon = (color) => {
  const map = { success: '\u{1F7E2}', danger: '\u{1F534}', warning: '\u{1F7E1}' };
  return map[color] || '\u{26AA}';
};

/* ─────────────────────────── Component ─────────────────────────── */

const ESGStewardshipIntelligence = () => {
  const [activeSection, setActiveSection] = useState('all');

  /* ---------- Section: Header / Status Bar ---------- */
  const HeaderSection = () => (
    <div className="mb-4">
      {/* Title Bar */}
      <div className="card border-0 shadow-sm mb-3" style={{ background: 'linear-gradient(135deg, #0d1b2a 0%, #1b263b 50%, #415a77 100%)' }}>
        <div className="card-body py-4 text-white">
          <div className="d-flex justify-content-between align-items-start flex-wrap">
            <div>
              <h2 className="mb-1 fw-bold">Trust Tech Studio&#8482; | Sovereign Intelligence Dashboard</h2>
              <div className="d-flex align-items-center gap-3 flex-wrap mt-2">
                <span className="badge bg-danger px-3 py-2">
                  <i className="bi bi-exclamation-triangle-fill me-1"></i>ACTION REQUIRED
                </span>
                <span className="opacity-75"><i className="bi bi-calendar-event me-1"></i>Updated: {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                <span className="badge bg-info px-3 py-2">
                  <i className="bi bi-shield-fill-check me-1"></i>S.I.L.E.N.C.E. Active
                </span>
              </div>
            </div>
            <div className="text-end mt-2 mt-md-0">
              <span className="badge bg-success fs-6 px-3 py-2">
                <i className="bi bi-broadcast me-1"></i>LIVE
              </span>
            </div>
          </div>
          <div className="mt-3 pt-3 border-top border-secondary">
            <blockquote className="mb-0 fst-italic opacity-75" style={{ fontSize: '0.95rem' }}>
              &ldquo;All that you touch, You Change. All that you Change, Changes you.&rdquo; &mdash; Octavia Butler
            </blockquote>
          </div>
        </div>
      </div>

      {/* IPPO Execution Nodes */}
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-header bg-white border-bottom">
          <h5 className="mb-0 fw-bold"><i className="bi bi-diagram-3 me-2"></i>IPPO Execution Nodes</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            {ippoNodes.map((node, idx) => (
              <div key={idx} className="col-md-6 col-lg-3">
                <div className="fw-bold text-primary mb-2">{node.group} &#8250;</div>
                <div className="d-flex flex-wrap gap-1">
                  {node.items.map((item, i) => (
                    <span key={i} className="badge bg-light text-dark border px-2 py-1" style={{ cursor: 'pointer', fontSize: '0.8rem' }}>
                      {item}{item === 'Ethics Fund' ? ' \u{1F512}' : ''}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  /* ---------- Section: DAML Workflow ---------- */
  const StabilizationSection = () => (
    <div className="card border-0 shadow-sm mb-4">
      <div className="card-header border-bottom" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' }}>
        <div className="d-flex align-items-center">
          <i className="bi bi-diagram-3 fs-4 text-info me-2"></i>
          <div>
            <h5 className="mb-0 fw-bold text-white">DAML Workflow Architecture</h5>
            <small className="text-white opacity-75">End-to-end transaction flow from asset origination to trust-verified settlement.</small>
          </div>
        </div>
      </div>
      <div className="card-body py-4">
        {/* Flow Arrow Strip */}
        <div className="d-flex align-items-stretch flex-wrap justify-content-center gap-0 mb-4">
          {damlWorkflow.map((node, idx) => (
            <React.Fragment key={node.step}>
              <a
                href={node.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-none text-white d-flex align-items-center justify-content-center text-center px-3 py-2"
                style={{ background: node.color, borderRadius: idx === 0 ? '8px 0 0 8px' : idx === damlWorkflow.length - 1 ? '0 8px 8px 0' : '0', minWidth: 140, flex: '1 1 0', cursor: 'pointer', transition: 'filter 0.2s' }}
                title={`Open ${node.title}`}
                onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.2)'}
                onMouseLeave={e => e.currentTarget.style.filter = 'brightness(1)'}
              >
                <i className={`bi ${node.icon} me-2 fs-5`}></i>
                <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{node.title}</span>
              </a>
              {idx < damlWorkflow.length - 1 && (
                <div className="d-flex align-items-center" style={{ background: '#e2e8f0', width: 28, justifyContent: 'center' }}>
                  <i className="bi bi-chevron-right text-dark fw-bold"></i>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Detail Cards */}
        <div className="row g-3">
          {damlWorkflow.map((node) => (
            <div key={node.step} className="col-md-6 col-lg">
              <div className="card border-0 h-100" style={{ borderTop: `3px solid ${node.color}` }}>
                <div className="card-body bg-light rounded-bottom">
                  <div className="d-flex align-items-center mb-2">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-2 text-white" style={{ width: 32, height: 32, minWidth: 32, background: node.color }}>
                      <i className={`bi ${node.icon}`} style={{ fontSize: '0.85rem' }}></i>
                    </div>
                    <h6 className="mb-0 fw-bold">{node.title}</h6>
                  </div>
                  <p className="text-muted mb-2" style={{ fontSize: '0.83rem' }}>{node.description}</p>
                  <a href={node.link} target="_blank" rel="noopener noreferrer" className="text-decoration-none small fw-semibold" style={{ color: node.color }}>
                    Open {node.title} <i className="bi bi-box-arrow-up-right ms-1"></i>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  /* ---------- Section: Account Dashboard (TTD Market) ---------- */
  const AccountDashboardSection = () => (
    <div className="card border-0 shadow-sm mb-4">
      <div className="card-header bg-white border-bottom">
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <div className="d-flex align-items-center">
            <i className="bi bi-graph-up-arrow fs-4 text-success me-2"></i>
            <div>
              <h5 className="mb-0 fw-bold">Account Dashboard: The People&rsquo;s Market (TTD)</h5>
              <small className="text-muted">Market Cap: {marketMetrics.marketCap} ({marketMetrics.globalEconomy} Global Economy) | Symbol: {marketMetrics.symbol}</small>
            </div>
          </div>
          <span className="badge bg-success fs-6 px-3 py-2 mt-2 mt-md-0">
            <i className="bi bi-circle-fill me-1" style={{ fontSize: '0.5rem', verticalAlign: 'middle' }}></i>
            {marketMetrics.status}
          </span>
        </div>
      </div>
      <div className="card-body">
        <h6 className="text-muted mb-3 fw-bold">Core Market Metrics (Live 2026)</h6>
        <div className="row g-3 mb-3">
          {/* Row 1 - Price / Trust / ESG */}
          <div className="col-md-4">
            <div className="card border-0 bg-primary bg-opacity-10 h-100">
              <div className="card-body text-center py-3">
                <div className="text-muted small fw-bold">TTD Price</div>
                <div className="h3 fw-bold text-primary mb-0">{marketMetrics.ttdPrice.value}</div>
                <span className="badge bg-success">{marketMetrics.ttdPrice.change}</span>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card border-0 bg-info bg-opacity-10 h-100">
              <div className="card-body text-center py-3">
                <div className="text-muted small fw-bold">TRUST Index</div>
                <div className="h3 fw-bold text-info mb-0">{marketMetrics.trustIndex.value}</div>
                <span className="badge bg-success">{marketMetrics.trustIndex.change}</span>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card border-0 bg-success bg-opacity-10 h-100">
              <div className="card-body text-center py-3">
                <div className="text-muted small fw-bold">ESG Index</div>
                <div className="h3 fw-bold text-success mb-0">{marketMetrics.esgIndex.value}</div>
                <span className="badge bg-success">{marketMetrics.esgIndex.change}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="row g-3">
          {/* Row 2 - Volume / Traders / Nodes */}
          <div className="col-md-4">
            <div className="card border-0 bg-light h-100">
              <div className="card-body text-center py-3">
                <div className="text-muted small fw-bold">Total Volume</div>
                <div className="h4 fw-bold text-dark mb-0">{marketMetrics.totalVolume}</div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card border-0 bg-light h-100">
              <div className="card-body text-center py-3">
                <div className="text-muted small fw-bold">Active Traders</div>
                <div className="h4 fw-bold text-dark mb-0">{marketMetrics.activeTraders}</div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card border-0 bg-light h-100">
              <div className="card-body text-center py-3">
                <div className="text-muted small fw-bold">Supplier Nodes</div>
                <div className="h4 fw-bold text-dark mb-0">{marketMetrics.supplierNodes.count}</div>
                <span className="badge bg-success">{marketMetrics.supplierNodes.change}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  /* ---------- Section: Genesis 13 Portfolio ---------- */
  const PortfolioSection = () => (
    <div className="card border-0 shadow-sm mb-4">
      <div className="card-header bg-white border-bottom">
        <div className="d-flex align-items-center">
          <i className="bi bi-briefcase-fill fs-4 text-primary me-2"></i>
          <div>
            <h5 className="mb-0 fw-bold">Portfolio Management &#8250; Genesis 13 Stakeholder View</h5>
            <small className="text-muted">Individualized tracking of the $31.025B AUM migration from Legacy Firms to Native Trust.</small>
          </div>
        </div>
      </div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead style={{ background: '#f0f4f8' }}>
              <tr>
                <th className="ps-3 py-3">Genesis Partner</th>
                <th className="py-3">Sovereign Identity</th>
                <th className="py-3">Native Trust Managed (HQLA)</th>
                <th className="py-3">Old World Exposure</th>
                <th className="py-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {genesisPartners.map((p, idx) => (
                <tr key={idx}>
                  <td className="ps-3 fw-bold">{p.partner}</td>
                  <td><small className="text-muted">{p.identity}</small></td>
                  <td>
                    <span className="fw-semibold text-success">{p.managed}</span>
                  </td>
                  <td>
                    <span className={p.exposure.includes('$0') ? 'text-muted' : 'text-danger fw-semibold'}>
                      {p.exposure}
                    </span>
                  </td>
                  <td className="text-center">
                    <span className={`badge bg-${p.color} px-2 py-1`}>
                      {statusIcon(p.color)} {p.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  /* ---------- Section: Governance Rights ---------- */
  const GovernanceSection = () => (
    <div className="card border-0 shadow-sm mb-4">
      <div className="card-header bg-white border-bottom">
        <div className="d-flex align-items-center">
          <i className="bi bi-bank2 fs-4 text-primary me-2"></i>
          <div>
            <h5 className="mb-0 fw-bold">Governance Rights Menu: The Future Has Arrived</h5>
          </div>
        </div>
      </div>
      <div className="card-body">
        <div className="row g-3">
          {governanceRights.map((right, idx) => (
            <div key={idx} className="col-md-6 col-lg-4">
              <div className="card border-0 bg-light h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: 36, height: 36, background: 'linear-gradient(135deg, #0d6efd, #6610f2)' }}>
                      <i className={`bi ${right.icon} text-white`}></i>
                    </div>
                    <h6 className="mb-0 fw-bold">{right.title}</h6>
                  </div>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.88rem' }}>{right.description}</p>
                  {right.link && (
                    <a href={right.link} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary mt-2">
                      <i className="bi bi-box-arrow-up-right me-1"></i>View Governance Hub
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  /* ---------- Section: Business Intelligence ---------- */
  const BusinessIntelSection = () => (
    <div className="card border-0 shadow-sm mb-4">
      <div className="card-header bg-white border-bottom">
        <div className="d-flex align-items-center">
          <i className="bi bi-search fs-4 text-primary me-2"></i>
          <div>
            <h5 className="mb-0 fw-bold">Business Intelligence &#8250; Dual Administrative Record Keeping Heritage</h5>
            <small className="text-muted">Comparative view of Old World on-balance-sheet structures vs. Native Trust real-world asset frameworks.</small>
          </div>
        </div>
      </div>
      <div className="card-body">
        <div className="row g-4">
          {/* Legacy Institutional Asset Flow */}
          <div className="col-md-6">
            <div className="card border-success h-100">
              <div className="card-header bg-success text-white">
                <h6 className="mb-0 fw-bold"><i className="bi bi-building me-2"></i>Legacy Institutional Asset Flow</h6>
              </div>
              <div className="card-body">
                <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>Able to ingest fully legible institutional records and translate them directly into settlement-finality conditions across compliant network rails.</p>
                <div className="mb-3 p-3 rounded" style={{ background: '#f0fdf4' }}>
                  <div className="fw-bold mb-2">Sovereign Metrics</div>
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small">Market Cap</span>
                      <span className="badge bg-success">$3.00T</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small">Global Economy Share</span>
                      <span className="badge bg-success">2.61%</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small">Risk-Weight (Basel IV)</span>
                      <span className="badge bg-success">0%</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small">Settlement Finality</span>
                      <span className="badge bg-success">Achievable</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small">Network Rail Access</span>
                      <span className="badge bg-success">Full</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Legacy Insulated Asset Flow */}
          <div className="col-md-6">
            <div className="card border-danger h-100">
              <div className="card-header bg-danger text-white">
                <h6 className="mb-0 fw-bold"><i className="bi bi-exclamation-octagon me-2"></i>Legacy Insulated Asset Flow</h6>
              </div>
              <div className="card-body">
                <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>Able to ingest illegible or incomplete records and convert them into legible, institution-ready formats through an administrative bridge, but unable to achieve settlement finality due to non-access to network rails.</p>
                <div className="mb-3 p-3 rounded" style={{ background: '#fff5f5' }}>
                  <div className="fw-bold mb-2">Legacy Risk Profile</div>
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small">Record Legibility</span>
                      <span className="badge bg-danger">Incomplete</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small">Administrative Bridge</span>
                      <span className="badge bg-warning text-dark">Required</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small">Settlement Finality</span>
                      <span className="badge bg-danger">Not Achievable</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small">Network Rail Access</span>
                      <span className="badge bg-danger">None</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small">Provenance Auditability</span>
                      <span className="badge bg-danger">Limited</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  /* ---------- Section: Clean Room Protocol ---------- */
  const CleanRoomSection = () => (
    <div className="card border-0 shadow-sm mb-4">
      <div className="card-header border-bottom" style={{ background: 'linear-gradient(135deg, #0d1b2a, #1b263b)' }}>
        <div className="d-flex align-items-center">
          <i className="bi bi-lock-fill fs-4 text-info me-2"></i>
          <h5 className="mb-0 fw-bold text-white">Clean Room Protocol | Gate 1 &amp; 2 Execution</h5>
        </div>
      </div>
      <div className="card-body">
        <div className="row g-4">
          {/* Gate 1 */}
          <div className="col-md-4">
            <div className="card border-primary h-100">
              <div className="card-header bg-primary text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold">Gate 1</span>
                  <span className="badge bg-light text-primary">Anchored</span>
                </div>
              </div>
              <div className="card-body">
                <p className="fw-semibold mb-2">Provenance Anchored (Native Tokens)</p>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                  Asset provenance verified and anchored on-chain via native token issuance with immutable ownership records.
                </p>
              </div>
            </div>
          </div>

          {/* Gate 2 */}
          <div className="col-md-4">
            <div className="card border-info h-100">
              <div className="card-header bg-info text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold">Gate 2</span>
                  <span className="badge bg-light text-info">Infrastructure</span>
                </div>
              </div>
              <div className="card-body">
                <p className="fw-semibold mb-2">Canton Settlement Infrastructure</p>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                  DAML smart contracts executing on Canton distributed ledger with privacy-preserving atomic settlement.
                </p>
              </div>
            </div>
          </div>

          {/* Live Priority */}
          <div className="col-md-4">
            <div className="card border-danger h-100">
              <div className="card-header bg-danger text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold">Live Priority</span>
                  <span className="badge bg-light text-danger">Active</span>
                </div>
              </div>
              <div className="card-body">
                <p className="fw-semibold mb-2">Activating Jurisdictional Corridor</p>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                  Establishing the legal and regulatory corridor for cross-jurisdictional settlement execution and compliance clearance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  /* ---------- Section: Settlement Flow ---------- */
  const SettlementFlowSection = () => {
    const completed = settlementStages.filter(s => s.status === 'Completed').length;
    const total = settlementStages.length;
    const pct = Math.round((completed / total) * 100);
    const severityBadge = { success: 'bg-success', warning: 'bg-warning text-dark', info: 'bg-info' };

    return (
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header border-bottom" style={{ background: 'linear-gradient(135deg, #1a1a2e, #16213e)' }}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <i className="bi bi-clipboard2-check fs-4 text-info me-2"></i>
              <div>
                <h5 className="mb-0 fw-bold text-white">Canton Settlement Flow | Audit Trail</h5>
                <small className="text-white opacity-75">End-to-end settlement pipeline powered by Canton. Stages 1-3 complete; Stages 4-6 tested via Mercury, now migrating to Canton.</small>
              </div>
            </div>
            <div className="text-end">
              <div className="text-white fw-bold">{completed}/{total} Stages</div>
              <small className="text-white opacity-75">{pct}% Complete</small>
            </div>
          </div>
        </div>
        <div className="card-body">
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="progress" style={{ height: '10px' }}>
              <div className="progress-bar bg-success" role="progressbar" style={{ width: `${pct}%` }} aria-valuenow={pct} aria-valuemin="0" aria-valuemax="100"></div>
            </div>
          </div>

          {/* Settlement Pipeline Stages */}
          <h6 className="fw-bold text-muted mb-3"><i className="bi bi-arrow-right-circle me-1"></i>Canton Settlement Pipeline</h6>
          <div className="row g-3 mb-4">
            {settlementStages.map((s) => (
              <div key={s.stage} className="col-md-6 col-lg-4">
                <div className={`card border-${s.color} h-100`}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="d-flex align-items-center">
                        <div className={`rounded-circle d-flex align-items-center justify-content-center me-2 bg-${s.color} ${s.color === 'warning' ? 'text-dark' : 'text-white'}`} style={{ width: 32, height: 32, minWidth: 32 }}>
                          <i className={`bi ${s.icon}`} style={{ fontSize: '0.85rem' }}></i>
                        </div>
                        <span className="fw-bold" style={{ fontSize: '0.9rem' }}>Stage {s.stage}</span>
                      </div>
                      <span className={`badge bg-${s.color} ${s.color === 'warning' ? 'text-dark' : ''}`}>{s.status}</span>
                    </div>
                    <h6 className="fw-bold mb-1">{s.title}</h6>
                    <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>{s.details}</p>
                    <small className="text-muted"><i className="bi bi-calendar-event me-1"></i>{s.date}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Audit Log */}
          <h6 className="fw-bold text-muted mb-3"><i className="bi bi-journal-text me-1"></i>Trust Tech IP — Governance &amp; Blockchain Path to Settlement</h6>
          <div className="table-responsive">
            <table className="table table-hover table-sm align-middle mb-0">
              <thead style={{ background: '#f0f4f8' }}>
                <tr>
                  <th className="ps-3 py-2">Date</th>
                  <th className="py-2">Actor</th>
                  <th className="py-2">Action</th>
                  <th className="py-2 text-center">Severity</th>
                </tr>
              </thead>
              <tbody>
                {settlementAuditLog.map((log, idx) => (
                  <tr key={idx}>
                    <td className="ps-3"><code style={{ fontSize: '0.8rem' }}>{log.timestamp}</code></td>
                    <td className="fw-semibold">{log.actor}</td>
                    <td style={{ fontSize: '0.88rem' }}>{log.action}</td>
                    <td className="text-center">
                      <span className={`badge ${severityBadge[log.severity] || 'bg-secondary'}`} style={{ fontSize: '0.75rem' }}>
                        {log.severity.toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  /* ---------- Section Navigation ---------- */
  const sections = [
    { id: 'all', label: 'Full Dashboard', icon: 'bi-grid-fill' },
    { id: 'stabilization', label: 'DAML Workflow', icon: 'bi-diagram-3' },
    { id: 'market', label: 'TTD Market', icon: 'bi-graph-up-arrow' },
    // { id: 'portfolio', label: 'Genesis 13', icon: 'bi-briefcase-fill' },
    { id: 'governance', label: 'Governance', icon: 'bi-bank2' },
    { id: 'intel', label: 'Business Intel', icon: 'bi-search' },
    { id: 'cleanroom', label: 'Clean Room', icon: 'bi-lock-fill' },
    { id: 'settlement', label: 'Settlement Flow', icon: 'bi-clipboard2-check' }
  ];

  const showSection = (id) => activeSection === 'all' || activeSection === id;

  /* ---------- Render ---------- */
  return (
    <div className="container-fluid py-4">
      {/* Always show header */}
      <HeaderSection />

      {/* Section Navigation Pills */}
      <div className="mb-4">
        <ul className="nav nav-pills flex-wrap gap-1">
          {sections.map(sec => (
            <li key={sec.id} className="nav-item">
              <button
                className={`nav-link ${activeSection === sec.id ? 'active' : ''}`}
                onClick={() => setActiveSection(sec.id)}
              >
                <i className={`bi ${sec.icon} me-1`}></i>{sec.label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Conditional Section Rendering */}
      {showSection('stabilization') && <StabilizationSection />}
      {showSection('market') && <AccountDashboardSection />}
      {/* {showSection('portfolio') && <PortfolioSection />} */}
      {showSection('governance') && <GovernanceSection />}
      {showSection('intel') && <BusinessIntelSection />}
      {showSection('cleanroom') && <CleanRoomSection />}
      {showSection('settlement') && <SettlementFlowSection />}
    </div>
  );
};

export default ESGStewardshipIntelligence;
